import {
  useEffect,
  useState,
  useRef,
  CSSProperties,
  useMemo,
  useCallback,
} from 'react';
//import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import { VideoBannerButtons } from './VideoBannerButtons';
import { Game } from 'functions/src/types/firestore/Game';
import { VideoBannerControls } from './VideoBannerControls';
import { DownloadGameDialog } from '../game-download/DownloadGameDialog';
import { DOWNLOAD_DIALOG_PROPS } from '../../util/game/downloadDialogue';
import { useRouter } from 'next/router';
import { useMobile } from 'src/hooks/useMobile';
import { VideoBannerDescription } from './VideoBannerDescription';
import { VideoBannerFrame } from './VideoBannerFrame';
import { toKebabCase } from 'src/util/string';
import { mod } from 'src/util/modBetter';
import { Typography } from '@mui/material';
import { GradientTypography } from '../gradients/GradientTypography';
import { useTheme } from '@mui/material/styles';
import { toImageKit } from 'functions/src/util/toImageKit';

export type VideoBannerGame = Pick<
  Game,
  | 'title'
  | 'videoUrl'
  | 'priority'
  | 'description'
  | 'tags'
  | 'likes'
  | 'platforms'
  | 'id'
> & {
  descriptionHeader?: string;
};

export type VideoBannerMedia = {
  videoUrl: string;
  title: string;
  description: string;
  descriptionHeader: string;
  tags: string[];
  likes: number;
  id: string;
};

const bannerStory: VideoBannerMedia = {
  title: 'BluMint',
  videoUrl: '/assets/video/brandStory.mp4',
  description: 'Own Your Game',
  descriptionHeader: 'BluMint',
  tags: [],
  likes: 0,
  id: '2',
};

export type VideoBannerProps = {
  games: VideoBannerGame[];
};

export function VideoBanner({ games }: VideoBannerProps) {
  const router = useRouter();
  const theme = useTheme();
  //const [videoLoaded, setVideoLoaded] = useState(false);
  const [activeGameIndex, setActiveGameIndex] = useState(0);
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [boxHeight, setBoxHeight] = useState(0);
  const [boxWidth, setBoxWidth] = useState(0);
  const videoMaskRef = useRef<HTMLDivElement>(null);
  const content = useMemo(() => {
    return [bannerStory, ...games];
  }, [games]);
  const [activeGame, setActiveGame] = useState<
    VideoBannerGame | VideoBannerMedia
  >(content[Number(activeGameIndex)]);

  useEffect(() => {
    const handleResize = () => {
      if (!!videoMaskRef && !!videoMaskRef.current) {
        setBoxHeight(videoMaskRef.current.clientHeight);
        setBoxWidth(videoMaskRef.current.clientWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      return window.removeEventListener('resize', handleResize);
    };
  }, []);

  const cssInsideBanner: CSSProperties = {
    zIndex: -1,
    position: 'absolute',
    top: -2,
    bottom: 0,
    right: 0,
    left: 2,
    WebkitClipPath: `path("M 90 0 H 90 L 0 85 V ${boxHeight - 50} L 40 ${
      boxHeight - 10
    } H 185 L 232 ${boxHeight - 55} H ${boxWidth - 245} L ${boxWidth - 185} ${
      boxHeight - 6
    } H ${boxWidth - 40} L ${boxWidth - 4} ${boxHeight - 50} V 85 L ${
      boxWidth - 94
    } 0 H 90")`,
  };

  const isMobile = useMobile();

  useEffect(() => {
    return setActiveGame(content[Number(activeGameIndex)]);
  }, [content, activeGameIndex]);
  const rotateActiveGame = useCallback(
    (diff: number) => {
      setActiveGameIndex((prev) => {
        return mod(prev + diff, content.length);
      });
    },
    [content.length],
  );

  const handlePlayGame = () => {
    switch (activeGame?.title) {
      case 'Undead Blocks':
        return setIsDownloadDialogOpen(true);
      default:
        return router.push(`/game/${toKebabCase(activeGame?.title)}`);
    }
  };
  return (
    <>
      {isMobile ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            zIndex: 1,
          }}
        >
          {isDownloadDialogOpen && (
            <DownloadGameDialog
              open={isDownloadDialogOpen}
              onClose={() => {
                return setIsDownloadDialogOpen(false);
              }}
              {...DOWNLOAD_DIALOG_PROPS[activeGame.title]}
            />
          )}
          <Box sx={{ position: 'relative', height: '375px' }}>
            <VideoBannerControls
              rotateActiveGame={rotateActiveGame}
              activeGameIndex={activeGameIndex}
              numberOfGames={content.length}
            />
            <video
              playsInline
              autoPlay
              muted
              src={toImageKit(activeGame.videoUrl)}
              //onLoadedData={() => setVideoLoaded(true)}
              onEnded={() => {
                return rotateActiveGame(1);
              }}
              style={{
                marginLeft: '-16px',
                marginRight: '-16px',
                height: 'calc(100% - 4px)',
                width: 'calc(100% + 32px)',
                borderBottom: `1px solid ${theme.palette.primary.dark}`,
              }}
            />
          </Box>
          <Box sx={{ pb: 8, pt: 2 }}>
            {activeGame.title !== 'BluMint' && (
              <VideoBannerDescription
                title={activeGame.title}
                description={activeGame.description}
                tags={activeGame.tags}
                likes={activeGame.likes}
                id={activeGame.id}
              />
            )}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            mb: '24px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              marginBottom: {
                xl: '10px',
                lg: 0,
                md: 0,
                sm: 0,
              },
              zIndex: 1,
            }}
          >
            {isDownloadDialogOpen && (
              <DownloadGameDialog
                open={isDownloadDialogOpen}
                onClose={() => {
                  return setIsDownloadDialogOpen(false);
                }}
                {...DOWNLOAD_DIALOG_PROPS[activeGame.title]}
              />
            )}

            <Box
              ref={videoMaskRef}
              sx={{
                width: '100%',
                height: '100%',
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  paddingTop: '56.25%',
                  display: 'block',
                  content: '""',
                },
              }}
            >
              <VideoBannerFrame />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '64px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  maxWidth: 'calc(100% - 620px)',
                }}
              >
                {activeGame.descriptionHeader && (
                  <GradientTypography
                    variant="h4"
                    gradientColor="primary.vertical"
                    sx={{
                      textTransform:
                        activeGame.descriptionHeader === 'BluMint'
                          ? undefined
                          : 'uppercase',
                    }}
                  >
                    {activeGame.descriptionHeader}
                  </GradientTypography>
                )}

                <Typography variant="subtitle1">
                  <Box
                    sx={{
                      ...theme.lineClamp(1),
                    }}
                  >
                    {activeGame.description.replaceAll('\\n', ' ')}
                  </Box>
                </Typography>
              </Box>
              <VideoBannerControls
                rotateActiveGame={rotateActiveGame}
                activeGameIndex={activeGameIndex}
                numberOfGames={content.length}
              />

              {/* {!videoLoaded && (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    ...CSS_INSIDE_BANNER,
                    height: '100%',
                  }}
                ></Skeleton>
              )} */}
              <video
                playsInline
                autoPlay
                muted
                src={toImageKit(activeGame.videoUrl)}
                //onLoadedData={() => setVideoLoaded(true)}
                onEnded={() => {
                  return rotateActiveGame(1);
                }}
                style={{
                  ...cssInsideBanner,
                  height: '100%',
                  width: '100%',
                }}
              />
              <VideoBannerButtons
                {...activeGame}
                handlePlayGame={handlePlayGame}
              />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
